import React, {useState} from 'react';
import Table from 'components/table/Table';
import TableHeader from 'components/table/TableHeader';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import {SearchType} from 'types/search';
import {useParams} from 'react-router-dom';
import {setActiveTab} from 'features/hr/hrSlice';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import {UserTrainingThunks} from 'features/userTraining/userTrainingThunks';
import {setIsEditing} from 'features/userTraining/userTrainingSlice';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import DotsMenu from 'components/DotsMenu';
import {UserTraining} from 'types/userTraining';
import WhiteButton from 'components/WhiteButton';
import DownloadIcon from 'assets/images/icons/DownloadIcon';
import useViewerModal from 'hooks/useViewerModal';
import StatusBadge from 'components/StatusBadge';
import usePermissions from '../../hooks/usePermissions';
import {useTableSelection} from 'hooks/useTableSelection';
import {useProcessDocuments} from 'hooks/useProcessDocuments';
import DownloadModal from 'components/DownloadModal';

const UserTrainingsTable: React.FC<{openDrawer: () => void}> = ({
  openDrawer,
}) => {
  const trainings = useSelector(
    (state: RootState) => state.userTraining.userTrainings,
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.userTraining.pagy);
  const loading = useSelector(
    (state: RootState) => state.userTraining.status === 'loading',
  );
  const params = useParams<{userId: string}>();
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const {
    isAllSelected,
    selectedIds: selectedTrainings,
    handleSelectAll,
    handleItemSelect: handleTrainingSelect,
  } = useTableSelection<UserTraining>(trainings);

  const {AttachmentModal, handleNameClick} = useViewerModal<UserTraining>({
    openDrawer,
    fetchItemThunk: UserTrainingThunks.show,
  });

  interface VisibleColumnsType {
    name: boolean;
    licensingAuthority: boolean;
    status: boolean;
    completedAt: boolean;
    expDate: boolean;
    userReminderDays: boolean;
    notes: boolean;
  }

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumnsType>({
    name: true,
    licensingAuthority: true,
    status: true,
    completedAt: true,
    expDate: true,
    userReminderDays: true,
    notes: true,
  });

  const searchByOptions = [{label: 'Name', value: 'name', operator: 'like'}];

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {
      label: 'Licensing Authority',
      value: 'licensingAuthority',
      isVisible: visibleColumns.licensingAuthority,
    },
    {label: 'Status', value: 'status', isVisible: visibleColumns.status},
    {
      label: 'Completion Date',
      value: 'completionDate',
      isVisible: visibleColumns.completedAt,
    },
    {label: 'Exp Date', value: 'expDate', isVisible: visibleColumns.expDate},
    {
      label: 'Reminder',
      value: 'userReminderDays',
      isVisible: visibleColumns.userReminderDays,
    },
    {label: 'Notes', value: 'notes', isVisible: visibleColumns.notes},
  ];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    const {userId} = params;

    search.where.user_id = {
      operator: '==',
      value: userId as string,
    };

    await dispatch(
      UserTrainingThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleDelete = async (id: string) => {
    const newTrainings = new UserTraining({
      id,
      attachment: null,
      deleteAttachment: true,
    });
    await dispatch(UserTrainingThunks.update(newTrainings));
    setDotsMenuIsOpen(null);
  };

  const handleEdit = async (id: string) => {
    await dispatch(UserTrainingThunks.show(id));
    dispatch(setIsEditing(true));
    openDrawer();
  };

  const handleDownload = (attachmentUrl: string) => {
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  };

  const selectedRecords = selectedTrainings
    .map(id => {
      const doc = trainings.find((d: UserTraining) => d.id === id);
      return doc ? {id: doc.id, documentType: 'Training'} : null;
    })
    .filter(Boolean) as {id: string; documentType: string}[];

  const openViewerModal = (cfg: any) => {
    handleNameClick(cfg);
  };

  const {
    isDownloadModalOpen,
    modalConfig,
    downloadStatus,
    processDocuments,
    setIsDownloadModalOpen,
    setDownloadStatus,
  } = useProcessDocuments(selectedRecords, openViewerModal);

  return (
    <>
      <Table
        tabs={[
          'HR Documents',
          'Certifications',
          'Trainings',
          'Background Documents',
          'Medical Documents',
          'Applications',
          'Roles',
          'Evaluations',
        ]}
        activeTab={'Trainings'}
        fetchData={fetchData}
        setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
        pagy={pagy}
        recordsName={'Trainings'}
        loading={loading}
        columns={true}
        columnOptions={columnOptions}
        onColumnToggle={handleColumnToggle}
        blueButton={
          checkPermission('create', 'HR') ? (
            <WhiteButton
              onClick={() => {
                if (selectedRecords.length > 0) processDocuments('download');
              }}
              label={''}
              icon={<DownloadIcon />}
            />
          ) : null
        }
        searchByOptions={searchByOptions}>
        <thead>
          <tr>
            <th scope="col" className="px-3 pt-3.5 flex justify-center">
              <input
                type="checkbox"
                className="shrink-0 border-stone-300 rounded disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-600 dark:checked:bg-green-500
                dark:checked:border-green-500 dark:focus:ring-offset-neutral-800"
                checked={isAllSelected}
                onChange={e => handleSelectAll(e.target.checked)}
              />
            </th>
            {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
            {visibleColumns.licensingAuthority && (
              <TableHeader label="Licensing Authority" />
            )}
            {visibleColumns.status && <TableHeader label="Status" />}
            {visibleColumns.completedAt && (
              <TableHeader label="Completion Date" />
            )}
            {visibleColumns.expDate && <TableHeader label="Exp Date" />}
            {visibleColumns.userReminderDays && (
              <TableHeader label="Reminder" />
            )}
            {visibleColumns.notes && <TableHeader label="Notes" />}
            <TableHeader label="" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
          {trainings.map((training: UserTraining) => {
            const userOptions = [];

            if (checkPermission('update', 'HR')) {
              userOptions.push({
                label: 'Edit',
                onClick: () => handleEdit(training.id),
                hoverClass: 'hover:bg-cyan-100 ',
              });

              if (training.status !== 'Missing') {
                userOptions.push({
                  label: 'Download',
                  onClick: () => handleDownload(training.attachment?.url || ''),
                  hoverClass: 'hover:bg-cyan-100',
                });
              }

              if (training.status === 'Missing') {
                userOptions.push({
                  label: 'Upload',
                  onClick: () => handleEdit(training.id),
                  hoverClass: 'hover:bg-cyan-100 ',
                });
              }
            }

            if (
              checkPermission('delete', 'HR') &&
              training.status !== 'Missing'
            ) {
              userOptions.push({
                label: 'Remove attached document',
                onClick: () => handleDelete(training.id),
                hoverClass: 'hover:bg-red-100 hover:text-red-800',
              });
            }
            return (
              <TableRow key={training.id}>
                <td className="pt-3.5 flex justify-center">
                  <input
                    type="checkbox"
                    className="border border-gray-300 rounded text-white disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-600
                                               dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                    checked={selectedTrainings.includes(training.id)}
                    onChange={e =>
                      handleTrainingSelect(training.id, e.target.checked)
                    }
                    onClick={e => e.stopPropagation()}
                  />
                </td>
                {visibleColumns.name && (
                  <TableCell>
                    <div className="relative group">
                      <div className="w-full flex items-center gap-x-3">
                        <div className="flex-grow">
                          {training.status !== 'Missing' &&
                          checkPermission('update', 'UserTraining') ? (
                            <span
                              className="text-sm cursor-pointer font-medium underline text-cyan-600 dark:text-neutral-200"
                              onClick={() => handleNameClick(training)}>
                              {training.name}
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                              {training.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                )}
                {visibleColumns.licensingAuthority && (
                  <TableCell>{training.licensingAuthority}</TableCell>
                )}
                {visibleColumns.status && (
                  <TableCell>
                    <StatusBadge status={training.status} />
                  </TableCell>
                )}
                {visibleColumns.completedAt && (
                  <TableCell>{training.completedAt}</TableCell>
                )}
                {visibleColumns.expDate && (
                  <TableCell>{training.expirationDate}</TableCell>
                )}
                {visibleColumns.userReminderDays && (
                  <TableCell>{training.userReminderDays || 'None'}</TableCell>
                )}
                {visibleColumns.notes && (
                  <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                    <div
                      style={{maxWidth: '150px'}}
                      className="whitespace-nowrap overflow-hidden text-ellipsis">
                      {training.notes}
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <DotsMenu options={userOptions} />
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
      <AttachmentModal />
      {isDownloadModalOpen && (
        <DownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => {
            setIsDownloadModalOpen(false);
            setDownloadStatus('preparing');
          }}
          status={downloadStatus}
          title={modalConfig?.title}
          customMessages={modalConfig?.customMessages}
          waitMessage={modalConfig?.waitMessage}
          buttonText={modalConfig?.buttonText}
        />
      )}
    </>
  );
};

export default UserTrainingsTable;
